import { useState, useRef, useCallback, useEffect } from "react";

const STATES = { IDLE: "idle", SELECTING: "selecting", SELECTED: "selected" };

function computeSelector(el) {
  if (!el) return "";
  if (el.id) return "#" + el.id;
  const tag = el.tagName ? el.tagName.toLowerCase() : "div";
  const firstClass = el.classList && el.classList[0] ? "." + el.classList[0] : "";
  return tag + firstClass;
}

export default function useElementSelector() {
  const [state, setState] = useState(STATES.IDLE);
  const [selectedElement, setSelectedElement] = useState(null);
  const [selector, setSelector] = useState("");

  const overlayRef = useRef(null);
  const highlightedRef = useRef(null);

  const removeOutline = useCallback(() => {
    if (highlightedRef.current) {
      try {
        highlightedRef.current.style.outline = "";
        highlightedRef.current.style.boxShadow = "";
      } catch (_) {}
      highlightedRef.current = null;
    }
  }, []);

  const cleanup = useCallback(() => {
    removeOutline();
    if (overlayRef.current && overlayRef.current.parentNode) {
      overlayRef.current.parentNode.removeChild(overlayRef.current);
    }
    overlayRef.current = null;
  }, [removeOutline]);

  const startSelecting = useCallback(() => {
    if (overlayRef.current) return;

    const overlay = document.createElement("div");
    overlay.style.cssText =
      "position:fixed;top:0;left:0;width:100%;height:100%;z-index:99998;" +
      "cursor:crosshair;background:transparent;";
    document.body.appendChild(overlay);
    overlayRef.current = overlay;

    setState(STATES.SELECTING);

    const onMove = (e) => {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;

      overlay.style.pointerEvents = "none";
      const target = document.elementFromPoint(clientX, clientY);
      overlay.style.pointerEvents = "";

      if (target && target !== overlay && target !== highlightedRef.current) {
        removeOutline();
        target.style.outline = "2px solid red";
        target.style.boxShadow = "0 0 0 2px red";
        highlightedRef.current = target;
      }
    };

    const onClick = (e) => {
      e.preventDefault();
      e.stopPropagation();

      const clientX = e.clientX;
      const clientY = e.clientY;

      overlay.style.pointerEvents = "none";
      const target = document.elementFromPoint(clientX, clientY);
      overlay.style.pointerEvents = "";

      const el = target && target !== overlay ? target : null;
      removeOutline();
      cleanup();

      if (el) {
        setSelectedElement(el);
        setSelector(computeSelector(el));
        setState(STATES.SELECTED);
      } else {
        setState(STATES.IDLE);
      }
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        removeOutline();
        cleanup();
        setState(STATES.IDLE);
        document.removeEventListener("keydown", onKeyDown);
        overlay.removeEventListener("mousemove", onMove);
        overlay.removeEventListener("touchmove", onMove);
        overlay.removeEventListener("click", onClick);
      }
    };

    overlay.addEventListener("mousemove", onMove);
    overlay.addEventListener("touchmove", onMove, { passive: true });
    overlay.addEventListener("click", onClick);
    document.addEventListener("keydown", onKeyDown);

    overlayRef.current._cleanup = () => {
      document.removeEventListener("keydown", onKeyDown);
      overlay.removeEventListener("mousemove", onMove);
      overlay.removeEventListener("touchmove", onMove);
      overlay.removeEventListener("click", onClick);
    };
  }, [cleanup, removeOutline]);

  const cancelSelecting = useCallback(() => {
    removeOutline();
    if (overlayRef.current && overlayRef.current._cleanup) {
      overlayRef.current._cleanup();
    }
    cleanup();
    setState(STATES.IDLE);
  }, [cleanup, removeOutline]);

  const reset = useCallback(() => {
    cancelSelecting();
    setSelectedElement(null);
    setSelector("");
    setState(STATES.IDLE);
  }, [cancelSelecting]);

  useEffect(() => {
    return () => {
      if (overlayRef.current && overlayRef.current._cleanup) {
        overlayRef.current._cleanup();
      }
      removeOutline();
      if (overlayRef.current && overlayRef.current.parentNode) {
        overlayRef.current.parentNode.removeChild(overlayRef.current);
      }
      overlayRef.current = null;
    };
  }, [removeOutline]);

  return { state, startSelecting, cancelSelecting, reset, selectedElement, selector };
}
